import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { MenuItem } from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAgGridEventState } from "shared/components/table/ag/AgGridEventState";
import {
  getAgColumnParents,
  isAgColumn,
} from "shared/components/table/ag/AgGridUtils";
import { DataTableColumnContext } from "shared/components/table/data/DataTableContext";
import { DataTableColumnMenuBase } from "shared/components/table/data/internal/DataTableColumnMenuBase";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import { EyeOffIcon } from "shared/icons/EyeOffIcon";
import { FilterIcon } from "shared/icons/FilterIcon";
import { RowGroupIcon } from "shared/icons/RowGroupIcon";

export const DataTableColumnMenu: React.FC<{
  columnContext: DataTableColumnContext;
}> = ({
  columnContext: { agColumnOrGroup: agColumn, columnId, column, rootColumn },
}) => {
  if (!isAgColumn(agColumn)) {
    return null;
  }

  const isHeader = column.is_header || rootColumn?.is_header;
  const menuItems: React.ReactElement[] = [];

  if (agColumn.isFilterAllowed()) {
    menuItems.push(<FilterColumnMenuItem key="filter" columnId={columnId} />);
  }

  if (column.enable_row_group) {
    menuItems.push(<GroupColumnMenuItem key="group" columnId={columnId} />);
  }

  if (!isHeader) {
    // Only show pin for top level columns
    const parentColumn = getAgColumnParents(agColumn).at(-1);
    const isTopLevel = !parentColumn?.getDefinition()?.headerName;
    const isLockPinned = agColumn.getColDef().lockPinned;
    if (isTopLevel && !isLockPinned) {
      menuItems.push(<PinColumnMenuItem key="pin" columnId={columnId} />);
    }
  }

  const isLockVisible =
    isAgColumn(agColumn) && agColumn.getColDef().lockVisible;
  if (!isLockVisible) {
    menuItems.push(<HideColumnMenuItem key="hide" columnId={columnId} />);
  }

  if (!menuItems.length) {
    return null;
  }

  return <DataTableColumnMenuBase>{menuItems}</DataTableColumnMenuBase>;
};

const FilterColumnMenuItem: React.FC<{
  columnId: string;
}> = ({ columnId }) => {
  const { t } = useTranslation(["common"]);
  const { agGrid } = useMaybernTableUIContext();
  return (
    <MenuItem
      icon={<FilterIcon w="16px" h="16px" />}
      onClick={() => {
        tryGetAgGridApi(agGrid)?.showColumnFilter(columnId);
      }}
    >
      {t("common:table.filter")}
    </MenuItem>
  );
};

const HideColumnMenuItem: React.FC<{
  columnId: string;
}> = ({ columnId }) => {
  const { t } = useTranslation(["common"]);
  const { agGrid } = useMaybernTableUIContext();
  return (
    <MenuItem
      icon={<EyeOffIcon w="16px" h="16px" />}
      onClick={() => {
        tryGetAgGridApi(agGrid)?.setColumnsVisible([columnId], false);
      }}
    >
      {t("common:table.hide")}
    </MenuItem>
  );
};

const PinColumnMenuItem: React.FC<{
  columnId: string;
}> = ({ columnId }) => {
  const { t } = useTranslation(["common"]);
  const { agGrid } = useMaybernTableUIContext();
  const isColumnPinned = useAgGridEventState(
    agGrid,
    "columnPinned",
    (api) => api.getColumn(columnId)?.isPinned() ?? false,
  );
  const IconComponent = isColumnPinned ? ArrowForwardIcon : ArrowBackIcon;
  const title = isColumnPinned
    ? t("common:table.unpin")
    : t("common:table.pin");
  return (
    <MenuItem
      icon={<IconComponent w="16px" h="16px" />}
      onClick={() => {
        tryGetAgGridApi(agGrid)?.setColumnsPinned([columnId], !isColumnPinned);
      }}
    >
      {title}
    </MenuItem>
  );
};

const GroupColumnMenuItem: React.FC<{
  columnId: string;
}> = ({ columnId }) => {
  const { t } = useTranslation(["common"]);
  const { agGrid } = useMaybernTableUIContext();
  const isRowGroup = useAgGridEventState(
    agGrid,
    "columnRowGroupChanged",
    (api) => api.getColumn(columnId)?.isRowGroupActive(),
  );
  return (
    <MenuItem
      icon={<RowGroupIcon />}
      onClick={() => {
        tryGetAgGridApi(agGrid)?.addRowGroupColumns([columnId]);
      }}
    >
      {isRowGroup ? t("common:table.ungroup") : t("common:table.group")}
    </MenuItem>
  );
};
